<template>
  <footer class="footer">
    <div class="footer-container">
      <div class="footer-content">
        <div class="footer-column">

        <img :src="logoUrl" alt="神翼轮胎" class="footer-logo-image" :class="{ loaded: logoLoaded }" @load="logoLoaded = true">
        </div>
        <div class="footer-column">
          <h3 class="footer-title">{{ $t('footer.products') }}</h3>
          <ul class="footer-links">
            <!-- 产品列表加载中 -->
            <li v-if="productsLoading"><span class="loading-text">{{ $t('common.loading') }}</span></li>

            <!-- 产品列表错误 -->
            <li v-else-if="productsError"><span class="error-text">{{ productsError }}</span></li>

            <!-- 无产品时显示 -->
            <li v-else-if="products.length === 0"><span class="empty-text">{{ $t('common.noData') }}</span></li>

            <!-- 动态显示产品列表 -->
            <li v-for="product in products" :key="product.id">
              <a :href="product.jumpLink" target="_blank">{{ product.productName }}</a>
            </li>
          </ul>
        </div>

        <div class="footer-column">
          <h3 class="footer-title">{{ $t('footer.about') }}</h3>
          <ul class="footer-links">
            <!-- 关于我们列表加载中 -->
            <li v-if="aboutLoading"><span class="loading-text">{{ $t('common.loading') }}</span></li>

            <!-- 关于我们列表错误 -->
            <li v-else-if="aboutError"><span class="error-text">{{ aboutError }}</span></li>

            <!-- 无关于我们内容时显示 -->
            <li v-else-if="aboutItems.length === 0"><span class="empty-text">{{ $t('common.noData') }}</span></li>

            <!-- 动态显示关于我们列表 -->
            <li v-for="item in formattedAboutItems" :key="item.id">
              <a v-if="item.jumpLink && item.jumpLink.startsWith('http')" :href="item.jumpLink" target="_blank">{{ item.content }}</a>
              <router-link v-else :to="item.jumpLink || '/about'">{{ item.content }}</router-link>
            </li>
          </ul>
        </div>

        <div class="footer-column">
          <h3 class="footer-title">{{ $t('footer.interact') }}</h3>
          <div class="qr-codes">
            <!-- QR codes loading state -->
            <div v-if="qrCodesLoading" class="loading-container">
              <span class="loading-text">{{ $t('common.loading') }}</span>
            </div>

            <!-- QR codes error state -->
            <div v-else-if="qrCodesError" class="error-container">
              <span class="error-text">{{ qrCodesError }}</span>
            </div>

            <!-- No QR codes available -->
            <div v-else-if="qrCodes.length === 0" class="empty-container">
              <span class="empty-text">{{ $t('common.noData') }}</span>
            </div>

            <!-- QR codes display -->
            <div v-else class="qr-codes">
              <div class="qr-code" v-for="(qrCode, index) in qrCodes" :key="qrCode.id">
                <span>{{ qrCode.content }}</span>
                <div class="qr-image">
                  <img :src="getQrCodeUrl(qrCode.qrUrl)" :alt="`QR Code ${index + 1}`" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="footerBase.footerContactUs" class="footer-contact">
        <p>{{ $t('footer.needHelp') }} <a :href="`tel:${footerBase.footerContactUs}`" class="phone-number">{{footerBase.footerContactUs}}</a></p>
      </div>
    </div>
      <!-- 加载状态 -->
      <div v-if="footerBaseLoading" class="loading-text">{{ $t('common.loading') }}</div>
      <!-- 正常显示 -->
      <div v-else class="footer-info">
        <span class="copyright">{{ footerBase.footerCopyright }}</span>
        <span v-if="footerBase.footerFilingInfo" class="divider">|</span>
        <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" class="icp-link">{{ footerBase.footerFilingInfo }}</a>
        <span v-if="footerBase.footerJumpLink"  class="divider">|</span>
        <a href="https://beian.mps.gov.cn/#/query/webSearch" target="_blank" class="icp-link">{{ footerBase.footerJumpLink }}</a>
      </div>
  </footer>
</template>

<script>
import QRCode from 'qrcode'
import { getHeaderInfo } from '@/api/home'

export default {
  name: 'AppFooter',
  data() {
    return {
      // Header信息相关数据
      headerInfo: {
        officialWebsiteLogo: '',
        jumpLink: '',
        externalWarIcon: ''
      },
      logoLoaded: false,

      // 产品相关数据
      products: [],
      productsLoading: false,
      productsError: null,

      // 关于我们相关数据
      aboutItems: [],
      aboutLoading: false,
      aboutError: null,

      // 二维码相关数据
      qrCodes: [],
      qrCodesLoading: false,
      qrCodesError: null,

      // 备案信息相关数据
      footerBase: {
        footerCopyright: '',    // 版权信息
        footerFilingInfo: '',   // ICP备案信息
        footerJumpLink: '',     // 公安备案信息
        footerContactUs: ''     // 服务热线
      },
      footerBaseLoading: false,
      footerBaseError: null
    };
  },
  computed: {
    logoUrl() {
      return this.headerInfo.officialWebsiteLogo
        ? process.env.VUE_APP_IMAGE_URL + this.headerInfo.officialWebsiteLogo
        : '@/assets/images/logo-blue.png';
    },
    formattedAboutItems() {
      return this.aboutItems.map(item => {
        if (item.jumpLink && !item.jumpLink.startsWith('http')) {
          return {
            ...item,
            jumpLink: `https://${item.jumpLink}`
          };
        }
        return item;
      });
    }
  },
  created() {
    this.fetchProductList();
    this.fetchAboutList();
    this.fetchQrCodes();
    this.fetchFooterBase();
    this.fetchHeaderInfo();
  },
  methods: {
    fetchProductList() {
      this.productsLoading = true;
      this.productsError = null;

      this.$api.product.getFooterProductList({
        pageNum: 1,
        pageSize: 5 // 只显示前5个产品
      })
        .then(response => {
          if (response.code === 200) {
            this.products = response.rows || [];
          } else {
            throw new Error(response.msg || '获取产品列表失败');
          }
        })
        .catch(err => {
          console.error('获取产品列表失败:', err);
          this.productsError = this.$t('common.error');
        })
        .finally(() => {
          this.productsLoading = false;
        });
    },

    fetchAboutList() {
      this.aboutLoading = true;
      this.aboutError = null;

      // 使用API模块调用
      this.$api.about.list({
        pageNum: 1,
        pageSize: 5 // 只显示前5条关于我们
      })
        .then(response => {
          if (response.code === 200) {
            this.aboutItems = response.rows || [];
            console.log('关于我们列表:', this.aboutItems);
          } else {
            throw new Error(response.msg || '获取关于我们列表失败');
          }
        })
        .catch(err => {
          console.error('获取关于我们列表失败:', err);
          this.aboutError = this.$t('common.error');
        })
        .finally(() => {
          this.aboutLoading = false;
        });
    },

    fetchQrCodes() {
      this.qrCodesLoading = true;
      this.qrCodesError = null;

      this.$api.qr.getQrList()
        .then(response => {
          if (response.code === 200) {
            this.qrCodes = response.rows || [];
            // Generate QR code images for each URL
            this.qrCodes.forEach(qrCode => {
              this.generateQrImage(qrCode);
            });
          } else {
            throw new Error(response.msg || '获取二维码列表失败');
          }
        })
        .catch(err => {
          console.error('获取二维码列表失败:', err);
          this.qrCodesError = this.$t('common.error');
        })
        .finally(() => {
          this.qrCodesLoading = false;
        });
    },

    generateQrImage(qrCode) {
      if (!qrCode.qrUrl) return;

      QRCode.toDataURL(qrCode.qrUrl, {
        width: 85,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
        .then(url => {
          // Use Vue.set to ensure reactivity
          this.$set(qrCode, 'qrImageUrl', url);
        })
        .catch(err => {
          console.error('QR码生成失败:', err);
        });
    },

    fetchFooterBase() {
      this.footerBaseLoading = true;
      this.footerBaseError = null;

      this.$api.home.getFootBase()
        .then(response => {
          if (response.code === 200 && response.data) {
            this.footerBase = {
              footerCopyright: response.data.footerCopyright || '',
              footerFilingInfo: response.data.footerFilingInfo || '',
              footerJumpLink: response.data.footerJumpLink || '',
              footerContactUs: response.data.footerContactUs || ''
            };
            console.log('备案信息:', this.footerBase);
          } else {
            throw new Error(response.msg || '获取备案信息失败');
          }
        })
        .catch(err => {
          console.error('获取备案信息失败:', err);
          this.footerBaseError = '加载失败';
        })
        .finally(() => {
          this.footerBaseLoading = false;
        });
    },

    getQrCodeUrl(url) {
      return process.env.VUE_APP_IMAGE_URL + url;
    },

    async fetchHeaderInfo() {
      try {
        const response = await getHeaderInfo();
        if (response && response.data) {
          this.headerInfo = {
            officialWebsiteLogo: response.data.officialWebsiteLogo,
            jumpLink: response.data.jumpLink,
            externalWarIcon: response.data.externalWarIcon
          };
        }
      } catch (error) {
        console.error('Error fetching header info:', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* Variables */
:root {
  --primary-color: #0056b3;
  --secondary-color: #faad14;
  --text-color: #333;
  --light-text: #fff;
  --dark-background: #0056b3;
  --section-padding: 80px 0;
  --border-radius: 4px;
}

/* Footer Styles */
.footer {
  background-color: #fff;
  color: #222;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  padding: 60px 0 40px 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.footer-column {
  flex: 1;
  margin-right: 30px;

  &:last-child {
    margin-right: 0;
  }

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 30px;
    width: 100%;
    text-align: center;
  }
}

.footer-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #222;
}
.icp-link {
  color: #eee;
}
.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 10px;

    a {
      color: #333;
      text-decoration: none;
      transition: all 0.3s;

      &:hover {
        color: #0056b3;
      }
    }
  }
}

.qr-codes {
  display: flex;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
}

.qr-code {
  display: flex;
  flex-direction: column;

  span {
    margin-bottom: 10px;
    color: #666;
  }
}

.qr-image {
  width: 85px;
  height: 85px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.qr-placeholder {
  width: 120px;
  height: 120px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    width: 80%;
    height: 80%;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  &:after {
    content: 'QR Code';
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
}

.footer-contact {
  text-align: center;
  padding: 20px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;

  p {
    font-size: 18px;
    color: #333;
  }

  .phone-number {
    color: #0056b3;
    font-weight: bold;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;
  border-top: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
}
.footer-logo-image {
  height: 40px;
  width: auto;
  display: block;
  margin-top: 20px;
  opacity: 0;
  transition: opacity 0.3s ease;

  &.loaded {
    opacity: 1;
  }
}


.social-icons {
  display: flex;
  gap: 15px;
}

.social-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #0056b3;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-3px);
    background-color: #003366;
  }

  &:before {
    content: '';
    width: 18px;
    height: 18px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }
}

/* 加载和错误状态样式 */
.loading-text, .error-text, .empty-text {
  color: #999;
  font-size: 14px;
  font-style: italic;
}

.error-text {
  color: #ff6b6b;
}

.empty-text {
  color: #bbb;
}

.loading-container, .error-container, .empty-container {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 10px 0;
}
.copyright {
  margin-top: 20px;
}
.footer-info {
  text-align: center;
  color: #eee;
  font-size: 16px;
  font-weight: 900;
  background-color: #0056b3;
  height: 70px;
  line-height: 70px;
}

.footer-info .divider {
  margin: 0 10px;
  color: #bbb;
}

.footer-info a {
  color: #eee;
  text-decoration: none;
  transition: opacity 0.3s;
}

.footer-info a:hover {
  opacity: 0.8;
}

@media (max-width: 768px) {
  .footer-container {
    padding: 0 8px !important;
    max-width: 100vw;
    box-sizing: border-box;
  }
  .footer-content {
    flex-direction: column !important;
    align-items: center;
    padding: 24px 0 12px 0 !important;
    gap: 0;
  }
  .footer-logo-image {
    margin: 0 auto 12px auto;
    width: 200px;
    height: auto;
    max-width: 80vw;
    display: block;
  }
  .footer-column {
    margin-right: 0 !important;
    margin-bottom: 14px;
    width: 100%;
    text-align: center;
    padding: 0;
  }
  .footer-title {
    font-size: 15px;
    margin-bottom: 10px;
  }
  .footer-links {
    font-size: 13px;
    word-break: break-all;
  }
  .qr-codes {
    flex-direction: row !important;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px !important;
    overflow-x: auto;
    width: 100%;
    padding-bottom: 8px;
  }
  .qr-code {
    align-items: center;
    margin-bottom: 0;
    min-width: 90px;
  }
  .qr-image {
    width: 64px !important;
    height: 64px !important;
  }
  .footer-contact {
    font-size: 13px;
    padding: 10px 0;
    border-top: 1px solid #eee;
    border-bottom: none;
  }
  .footer-info {
    font-size: 11px;
    padding: 0 2px;
    height: auto;
    line-height: 1.6;
    background: #0056b3;
    color: #fff;
    font-weight: 400;
  }
  .footer-info .divider {
    margin: 0 2px;
  }
  .copyright {
    margin-top: 0;
  }
}
</style>
